import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { ASSUMPTION_GROUPS, fmt$, fmtPct, fmtX } from "../../lib/deckModel";
import { apiError } from "../../lib/api";
import { uploadDeckImage } from "./CreateDeckDialog";
import { MarketEditor } from "./MarketEditor";
import { SiteEditor } from "./PropertySite";
import { VideoEditor } from "./HeroVideo";

const inp = "w-full rounded-md border border-border bg-secondary/30 px-2.5 py-1.5 text-sm outline-none focus:border-primary";
const Label = ({ children }) => <label className="text-[10px] uppercase tracking-wide text-muted-foreground">{children}</label>;

export function SectionEditor({ section, onChange }) {
  const c = section.content || { body: "", bullets: [] };
  const setC = (patch) => onChange({ ...section, content: { ...c, ...patch } });
  const bullets = c.bullets || [];
  return (
    <div className="space-y-4" data-testid="deck-section-editor">
      <div className="space-y-1"><Label>Section title</Label>
        <input value={section.title} onChange={(e) => onChange({ ...section, title: e.target.value })} className={inp} data-testid="deck-section-title-input" /></div>
      <div className="space-y-1"><Label>Headline (serif title)</Label>
        <input value={c.headline || ""} placeholder={section.title} onChange={(e) => setC({ headline: e.target.value })} className={inp} data-testid="deck-section-headline-input" /></div>
      <div className="space-y-1"><Label>Subtitle</Label>
        <input value={c.subtitle || ""} onChange={(e) => setC({ subtitle: e.target.value })} className={inp} data-testid="deck-section-subtitle-input" /></div>
      <div className="space-y-1"><Label>Lead paragraph</Label>
        <textarea rows={6} value={c.body || ""} onChange={(e) => setC({ body: e.target.value })} className={inp} data-testid="deck-section-body-input" /></div>
      <div className="space-y-1.5"><Label>Key points</Label>
        {bullets.map((b, i) => (
          <div key={i} className="flex gap-1.5">
            <input value={b} onChange={(e) => setC({ bullets: bullets.map((x, j) => (j === i ? e.target.value : x)) })} className={inp} data-testid={`deck-bullet-${i}`} />
            <button onClick={() => setC({ bullets: bullets.filter((_, j) => j !== i) })} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <button onClick={() => setC({ bullets: [...bullets, ""] })} className="flex items-center gap-1 text-xs text-primary" data-testid="deck-add-bullet"><Plus className="h-3.5 w-3.5" />Add point</button>
      </div>
      <p className="text-[11px] text-muted-foreground">Numbers in this section come from Project Assumptions and update automatically.</p>
      {section.type === "market_leasing" && <MarketEditor market={c.market} onChange={(market) => setC({ market })} />}
      {["property_overview", "site_feasibility"].includes(section.type) && <SiteEditor site={c.site} onChange={(site) => setC({ site })} />}
    </div>
  );
}

const PROJECT_FIELDS = [["name", "Project name"], ["tagline", "Tagline"], ["project_type", "Project type"], ["stage", "Project stage"],
  ["address", "Address"], ["city", "City"], ["state", "State"], ["county", "County"], ["apn", "APN"], ["owner", "Property owner"], ["acreage", "Acreage"]];

export function ProjectPanel({ project, onChange }) {
  const set = (k, v) => onChange({ ...project, [k]: v });
  const onCover = async (f) => { if (!f) return; try { set("cover_image", await uploadDeckImage(f)); toast.success("Cover image uploaded"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="deck-project-panel">
      {PROJECT_FIELDS.map(([k, l]) => (
        <div key={k} className={`space-y-1 ${["name", "tagline", "address"].includes(k) ? "col-span-2" : ""}`}><Label>{l}</Label>
          <input value={project[k] || ""} onChange={(e) => set(k, e.target.value)} className={inp} data-testid={`deck-project-${k}`} /></div>
      ))}
      <label className="col-span-2 flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40" data-testid="deck-project-cover">
        <Upload className="h-4 w-4" />{project.cover_image ? "Replace cover image" : "Upload cover image"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onCover(e.target.files?.[0])} />
      </label>
      <div className="col-span-2"><VideoEditor video={project.video} onChange={(video) => set("video", video)} /></div>
    </div>
  );
}

export function AssumptionsPanel({ a, onChange, model }) {
  return (
    <div className="space-y-5" data-testid="deck-assumptions-panel">
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
        {[["NOI", fmt$(model.noi)], ["Value", fmt$(model.value)], ["Total cost", fmt$(model.totalCost)], ["Yield on cost", fmtPct(model.yoc, 2)],
          ["Debt", fmt$(model.debt)], ["Equity", fmt$(model.equity)]].map(([k, v]) => (
          <div key={k}><div className="text-muted-foreground">{k}</div><div className="font-semibold tabular-nums" data-testid={`deck-live-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</div></div>
        ))}
      </div>
      {ASSUMPTION_GROUPS.map((g) => (
        <div key={g.title} className="space-y-2">
          <div className="text-xs font-semibold">{g.title}</div>
          <div className="grid grid-cols-2 gap-2">
            {g.fields.map(([k, l, t]) => (
              <div key={k} className="space-y-1"><Label>{l}{t === "pct" ? " (%)" : t === "money" ? " ($)" : ""}</Label>
                <input inputMode="decimal" value={a[k] ?? ""} onChange={(e) => onChange({ ...a, [k]: e.target.value })} className={`${inp} tabular-nums`} data-testid={`deck-assumption-${k}`} /></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ExitsPanel({ exits, onChange, model }) {
  const set = (i, patch) => onChange(exits.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const computed = Object.fromEntries(model.exits.map((e) => [e.key, e]));
  return (
    <div className="space-y-2.5" data-testid="deck-exits-panel">
      <p className="text-[11px] text-muted-foreground">Build + Sell, Build + Hold and Refinance pull cost and value from the model. Earlier exits use the cost-to-date and exit value you enter.</p>
      {exits.map((e, i) => {
        const c = computed[e.key];
        return (
          <div key={e.key} className={`rounded-lg border p-3 space-y-2 ${e.enabled === false ? "border-border opacity-60" : "border-border"}`} data-testid={`deck-exit-cfg-${e.key}`}>
            <label className="flex items-center justify-between text-sm font-medium">
              <span>{e.label}</span>
              <input type="checkbox" checked={e.enabled !== false} onChange={(x) => set(i, { enabled: x.target.checked })} data-testid={`deck-exit-enabled-${e.key}`} />
            </label>
            {e.enabled !== false && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1"><Label>Cost to date ($)</Label><input value={e.cumulative_cost ?? ""} placeholder={c?.auto ? "auto" : ""} onChange={(x) => set(i, { cumulative_cost: x.target.value })} className={inp} data-testid={`deck-exit-cost-${e.key}`} /></div>
                  <div className="space-y-1"><Label>Exit value ($)</Label><input value={e.exit_value ?? ""} placeholder={c?.auto ? "auto" : ""} onChange={(x) => set(i, { exit_value: x.target.value })} className={inp} data-testid={`deck-exit-value-${e.key}`} /></div>
                  <div className="space-y-1"><Label>Months</Label><input value={e.months ?? ""} onChange={(x) => set(i, { months: x.target.value })} className={inp} data-testid={`deck-exit-months-${e.key}`} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>What is sold</Label><input value={e.sold || ""} onChange={(x) => set(i, { sold: x.target.value })} className={inp} /></div>
                  <div className="space-y-1"><Label>What is retained</Label><input value={e.retained || ""} onChange={(x) => set(i, { retained: x.target.value })} className={inp} /></div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <select value={e.risk || ""} onChange={(x) => set(i, { risk: x.target.value })} className={`${inp} w-36`}>{["", "Low", "Medium", "High"].map((r) => <option key={r} value={r}>{r ? `${r} risk` : "Risk…"}</option>)}</select>
                  <div className="text-[11px] text-right tabular-nums text-muted-foreground" data-testid={`deck-exit-summary-${e.key}`}>Net {c?.ready ? fmt$(c.net) : "—"} · {fmtX(c?.em)} · IRR {fmtPct(c?.irr)}</div>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function VersionPanel({ deck }) {
  const d = (v) => (v ? new Date(v).toLocaleString() : "—");
  return (
    <div className="space-y-2 text-sm" data-testid="deck-version-panel">
      {[["Revision", deck.revision], ["Last saved", d(deck.last_saved_at)], ["Last published", d(deck.last_published_at)], ["Published revision", deck.published_revision ?? "—"], ["Updated by", deck.updated_by || "—"]].map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-border/60 py-1.5"><span className="text-muted-foreground">{k}</span><span data-testid={`deck-version-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</span></div>
      ))}
      <p className="text-[11px] text-muted-foreground pt-2">A snapshot is kept every time you publish, ready for “Restore previous version” later.</p>
    </div>
  );
}
